import { createFileRoute, Link } from '@tanstack/react-router'
import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FileDrop } from '@/components/file-drop'
import { Th, Td, fmt, fmtDateTime } from '@/components/data-table'
import { Trash2, AlertTriangle } from 'lucide-react'
import type { Adjustments } from '@/lib/portal-cautivo-clean'

export const Route = createFileRoute('/_protected/admin/cargas')({
  component: CargasPage,
})

type YaCargado = {
  uploadFileName: string
  uploadedAt: number
  filas: number
  sesiones: number
  usuarios: number
}

type InformeAnexo = {
  fileName: string
  periodo: string
  mes: string
  zonas: number
  filas: number
  sesiones: number
  usuarios: number
  adjustments: Adjustments
  yaCargado: YaCargado[]
}

type Upload = {
  uploadFileName: string
  uploadedAt: number
  totalZonas: number
  totalSesiones: number
  totalUsuarios: number
}

function CargasPage() {
  const [file, setFile] = React.useState<File | null>(null)
  const [token, setToken] = React.useState<string | null>(null)
  const [informe, setInforme] = React.useState<InformeAnexo | null>(null)
  const [reemplazar, setReemplazar] = React.useState(true)
  const [fase, setFase] = React.useState<'inicio' | 'analizando' | 'revision' | 'cargando'>('inicio')
  const [error, setError] = React.useState<string | null>(null)
  const [exito, setExito] = React.useState<string | null>(null)
  const [deletingKey, setDeletingKey] = React.useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: uploads, isLoading } = useQuery({
    queryKey: ['pc-uploads'],
    queryFn: async () => {
      const res = await fetch('/api/portal-cautivo/uploads', { credentials: 'include' })
      return res.json() as Promise<Upload[]>
    },
  })

  function reset() {
    setFile(null); setToken(null); setInforme(null)
    setFase('inicio'); setError(null); setReemplazar(true)
  }

  async function analizar(f: File) {
    setFile(f); setError(null); setExito(null); setInforme(null); setToken(null)
    setFase('analizando')
    try {
      const fd = new FormData()
      fd.append('file', f)
      fd.append('tipo', 'anexo')
      const res = await fetch('/api/admin/preview', { method: 'POST', body: fd, credentials: 'include' })
      const data = await res.json()
      if (!res.ok) { setError(data.error ?? `Error ${res.status}`); setFase('inicio'); return }
      setToken(data.token)
      setInforme(data.informe)
      setFase('revision')
    } catch {
      setError('Error de red al analizar el archivo')
      setFase('inicio')
    }
  }

  async function confirmar() {
    if (!token || !informe) return
    setFase('cargando'); setError(null)
    try {
      const res = await fetch('/api/admin/commit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          tipo: 'anexo',
          fileName: informe.fileName,
          reemplazar: reemplazar
            ? informe.yaCargado.map((y) => ({ uploadFileName: y.uploadFileName, uploadedAt: y.uploadedAt }))
            : [],
        }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error ?? `Error ${res.status}`); setFase('revision'); return }
      setExito(
        `${fmt(data.insertadas)} filas cargadas` +
        (data.reemplazadas ? `, ${data.reemplazadas} carga(s) anterior(es) reemplazada(s)` : '') +
        `. Respaldo en ${data.respaldo}`,
      )
      queryClient.invalidateQueries({ queryKey: ['pc-uploads'] })
      reset()
    } catch {
      setError('Error de red al cargar')
      setFase('revision')
    }
  }

  async function borrar(u: Upload) {
    if (!confirm(`¿Eliminar "${u.uploadFileName}"?\nEsto borrará todos sus datos y no se puede deshacer.`)) return
    const key = `${u.uploadFileName}-${u.uploadedAt}`
    setDeletingKey(key)
    try {
      const p = new URLSearchParams({ file: u.uploadFileName, uploadedAt: String(u.uploadedAt) })
      await fetch(`/api/portal-cautivo/uploads?${p}`, { method: 'DELETE', credentials: 'include' })
      queryClient.invalidateQueries({ queryKey: ['pc-uploads'] })
    } finally {
      setDeletingKey(null)
    }
  }

  const a = informe?.adjustments

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Cargar un Anexo del Portal Cautivo</CardTitle>
          <p className="text-sm text-muted-foreground">
            El periodo se detecta del contenido del archivo. Nada se escribe hasta que confirmes.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <FileDrop
            file={file}
            disabled={fase === 'analizando' || fase === 'cargando'}
            onSelect={(f) => { if (f) analizar(f); else reset() }}
            onError={setError}
          />

          {fase === 'analizando' && (
            <p className="text-sm text-blue-700">Analizando el archivo…</p>
          )}

          {informe && fase !== 'cargando' && (
            <div className="border rounded-lg divide-y">
              <div className="p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                <Dato k="Periodo" v={informe.periodo} />
                <Dato k="Zonas" v={String(informe.zonas)} />
                <Dato k="Filas" v={fmt(informe.filas)} />
                <Dato k="Sesiones" v={fmt(informe.sesiones)} />
                <Dato k="Usuarios" v={fmt(informe.usuarios)} />
              </div>

              {a && (
                <div className="p-4 text-sm space-y-1">
                  <p className="font-medium">Correcciones que se aplicarán</p>
                  {a.itemsFixed + a.datesFixed + a.sheetsShifted + a.countsCoerced === 0 ? (
                    <p className="text-muted-foreground">El archivo no requiere correcciones.</p>
                  ) : (
                    <ul className="list-disc pl-5 text-muted-foreground">
                      {a.itemsFixed > 0 && <li>{a.itemsFixed} filas con el código de otra zona</li>}
                      {a.sheetsShifted > 0 && <li>{a.sheetsShifted} hoja(s) fechada(s) en otro mes</li>}
                      {a.datesFixed > 0 && <li>{a.datesFixed} fechas reubicadas</li>}
                      {a.countsCoerced > 0 && <li>{a.countsCoerced} valores numéricos con texto</li>}
                    </ul>
                  )}
                  {(a.droppedOffMonth > 0 || a.droppedNoDate > 0) && (
                    <p className="text-amber-700">
                      Se descartarán {a.droppedOffMonth + a.droppedNoDate} filas
                      {a.droppedOffMonth > 0 && ` (${fmt(a.droppedSesiones)} sesiones, ${fmt(a.droppedUsuarios)} usuarios)`}.
                    </p>
                  )}
                  {a.unresolvedIdentity.length > 0 && (
                    <p className="text-amber-700">
                      Zonas sin municipio/nombre identificable: {a.unresolvedIdentity.join(', ')}
                    </p>
                  )}
                </div>
              )}

              {informe.yaCargado.length > 0 && (
                <div className="p-4 text-sm space-y-2 bg-amber-50">
                  <p className="font-medium text-amber-900 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Ya hay datos cargados de {informe.periodo}
                  </p>
                  <ul className="text-amber-900/80 list-disc pl-5">
                    {informe.yaCargado.map((y) => (
                      <li key={`${y.uploadFileName}-${y.uploadedAt}`}>
                        {y.uploadFileName} — {fmt(y.filas)} filas, {fmt(y.sesiones)} sesiones
                      </li>
                    ))}
                  </ul>
                  <label className="flex items-start gap-2 pt-1">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={reemplazar}
                      onChange={(e) => setReemplazar(e.target.checked)}
                    />
                    <span className="text-amber-900">
                      Reemplazar la(s) carga(s) anterior(es).{' '}
                      <strong>
                        Si no lo marcas, ese mes quedará duplicado en todos los informes
                      </strong>{' '}
                      — las consultas filtran por rango de fechas, no por archivo.
                    </span>
                  </label>
                </div>
              )}

              <div className="p-4 flex gap-2 justify-end">
                <Button variant="outline" onClick={reset}>Cancelar</Button>
                <Button onClick={confirmar}>Confirmar y cargar</Button>
              </div>
            </div>
          )}

          {fase === 'cargando' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
              <span className="text-blue-700 font-medium">Cargando, no cierres la página…</span>
            </div>
          )}

          {exito && (
            <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
              {exito}
            </div>
          )}
          {error && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>Anexos cargados</CardTitle>
          <Link to="/historial" className="text-sm text-primary hover:underline">
            Ver detalle por zona y día →
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Archivo</Th><Th>Fecha de carga</Th>
                    <Th right>Zonas</Th><Th right>Sesiones</Th><Th right>Usuarios</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {uploads?.map((u) => {
                    const key = `${u.uploadFileName}-${u.uploadedAt}`
                    return (
                      <tr key={key} className="hover:bg-slate-50">
                        <Td>{u.uploadFileName}</Td>
                        <Td>{fmtDateTime(u.uploadedAt)}</Td>
                        <Td right>{fmt(u.totalZonas)}</Td>
                        <Td right>{fmt(u.totalSesiones)}</Td>
                        <Td right>{fmt(u.totalUsuarios)}</Td>
                        <td className="px-2 py-2 text-right border-b">
                          <Button
                            variant="ghost" size="sm"
                            disabled={deletingKey === key}
                            onClick={() => borrar(u)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                  {uploads?.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-sm text-center text-muted-foreground">
                      No hay Anexos cargados.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function Dato({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{k}</p>
      <p className="font-semibold">{v}</p>
    </div>
  )
}
